import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { apiFetch } from '../../services/apiClient';

// Task checklists, editable here in Settings. The field Ask AI reads these so a
// worker can ask "give me a checklist for <task>" and get the company's steps.
// Load-up lists live here too (Ryan, Oct 27 2026): what to load for a kind of job, dropped onto a
// shop order with "📋 Insert Load-up List" — each line then gets a Shop and a Field check mark.
const KINDS = { task: { one: 'Checklist', many: 'Checklists', color: '#0e7490', lines: 'Steps — one per line', count: 'step' },
  loadup: { one: 'Load-up List', many: 'Load-up Lists', color: '#6d28d9', lines: 'What to load — one per line (what & how many)', count: 'item' } };
export default function AdminChecklists() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, title, category, keywords, itemsText }
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');   // all | task | loadup

  function load() {
    setLoading(true);
    apiFetch('/checklists').then(d => setList(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  function openNew(kind = 'task') { setErr(''); setEditing({ kind, title: '', category: '', keywords: '', itemsText: '' }); }
  function openEdit(c) { setErr(''); setEditing({ id: c.id, kind: c.kind || 'task', title: c.title || '', category: c.category || '', keywords: c.keywords || '', itemsText: (c.items || []).join('\n') }); }

  async function save() {
    const e = editing;
    if (!e.title.trim()) { setErr(`Give the ${KINDS[e.kind].one.toLowerCase()} a title.`); return; }
    const items = e.itemsText.split('\n').map(s => s.trim()).filter(Boolean);
    if (items.length === 0) { setErr(`Add at least one ${KINDS[e.kind].count} (one per line).`); return; }
    setSaving(true); setErr('');
    try {
      const body = JSON.stringify({ kind: e.kind, title: e.title.trim(), category: e.category.trim(), keywords: e.keywords.trim(), items });
      if (e.id) await apiFetch(`/checklists/${e.id}`, { method: 'PATCH', body });
      else await apiFetch('/checklists', { method: 'POST', body });
      setEditing(null); load();
    } catch (er) { setErr(er?.error || er?.message || 'Could not save — try again.'); }
    setSaving(false);
  }
  async function del(c) {
    if (!window.confirm(`Delete the "${c.title}" ${KINDS[c.kind || 'task'].one.toLowerCase()}?`)) return;
    try { await apiFetch(`/checklists/${c.id}`, { method: 'DELETE' }); setList(l => l.filter(x => x.id !== c.id)); } catch {}
  }

  const ql = q.trim().toLowerCase();
  const shown = list.filter(c => (show === 'all' || (c.kind || 'task') === show) && (!ql || `${c.title} ${c.category || ''} ${c.keywords || ''}`.toLowerCase().includes(ql)));
  const count = k => list.filter(c => (c.kind || 'task') === k).length;

  const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 };
  const inp = { width: '100%', boxSizing: 'border-box', border: '1.5px solid var(--border)', borderRadius: 8, padding: '9px 11px', fontSize: 14, background: 'var(--surface)', color: 'var(--text)' };
  const lbl = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 5 };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Checklists &amp; Load-up Lists</h1>
          <p className="admin-page-sub">Checklists your crew can ask the field AI for — “give me a checklist for setting a pole foundation.” Load-up lists drop onto a shop order with 📋 Insert Load-up List, with a Shop and a Field check mark on every line.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="admin-primary-btn" onClick={() => openNew('task')}>+ New Checklist</button>
          <button className="admin-primary-btn" onClick={() => openNew('loadup')} style={{ background: '#6d28d9' }}>+ New Load-up List</button>
        </div>
      </div>
      <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden', marginBottom: 12 }}>
        {[['all', `All (${list.length})`], ['task', `Checklists (${count('task')})`], ['loadup', `Load-up Lists (${count('loadup')})`]].map(([k, l]) => (
          <button key={k} onClick={() => setShow(k)} style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 700, border: 'none', cursor: 'pointer', background: show === k ? '#111827' : 'var(--surface)', color: show === k ? '#fff' : 'var(--text)' }}>{l}</button>
        ))}
      </div>

      <div style={{ marginBottom: 14, maxWidth: 360, position: 'relative', display: 'flex', alignItems: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 11, color: 'var(--text-muted)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search checklists and load-up lists…"
          style={{ ...inp, padding: '8px 12px 8px 32px', borderRadius: 999 }} />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : shown.length === 0 ? (
        <div style={{ ...card, borderStyle: 'dashed', padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
          {list.length === 0 ? 'No checklists yet. Add your first one — the field AI will start handing it out.' : q ? `Nothing matches “${q}”.` : show === 'loadup' ? 'No load-up lists yet — “+ New Load-up List” starts one.' : 'Nothing here yet.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {shown.map(c => (
            <div key={c.id} style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{c.title}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, marginTop: 1 }}>
                    <span style={{ color: KINDS[c.kind || 'task'].color }}>{c.kind === 'loadup' ? '📋 Load-up list' : '✅ Checklist'}</span>
                    {c.category && <span style={{ color: '#0e7490' }}> · {c.category}</span>}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{c.items.length} {KINDS[c.kind || 'task'].count}{c.items.length === 1 ? '' : 's'}</span>
              </div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxHeight: 132, overflow: 'hidden' }}>
                {c.items.slice(0, 6).map((it, i) => <li key={i}>{it}</li>)}
                {c.items.length > 6 && <li style={{ listStyle: 'none', color: '#9ca3af' }}>+{c.items.length - 6} more…</li>}
              </ol>
              {c.keywords && <div style={{ fontSize: 11, color: '#9ca3af' }}>Also called: {c.keywords}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <button onClick={() => openEdit(c)} style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Edit</button>
                <button onClick={() => del(c)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginLeft: 'auto' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div onClick={() => setEditing(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14, width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.28)' }}>
            <div style={{ background: KINDS[editing.kind].color, color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '14px 14px 0 0' }}>
              <span style={{ fontWeight: 800, fontSize: 15 }}>{editing.id ? 'Edit' : 'New'} {KINDS[editing.kind].one}</span>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {['task', 'loadup'].map(k => (
                  <button key={k} type="button" onClick={() => setEditing(s => ({ ...s, kind: k }))}
                    style={{ padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 800, cursor: 'pointer', border: `1.5px solid ${KINDS[k].color}`, background: editing.kind === k ? KINDS[k].color : 'transparent', color: editing.kind === k ? '#fff' : KINDS[k].color }}>
                    {k === 'loadup' ? '📋 Load-up list' : '✅ Checklist'}
                  </button>
                ))}
              </div>
              <div>
                <label style={lbl}>Title *</label>
                <input autoFocus value={editing.title} onChange={e => setEditing(s => ({ ...s, title: e.target.value }))} placeholder="e.g. Set a Signal Pole Foundation" style={inp} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={lbl}>Category (optional)</label>
                  <input value={editing.category} onChange={e => setEditing(s => ({ ...s, category: e.target.value }))} placeholder="Drilling, Signals, Safety…" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Also called / keywords</label>
                  <input value={editing.keywords} onChange={e => setEditing(s => ({ ...s, keywords: e.target.value }))} placeholder="pole base, foundation, footing" style={inp} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -6 }}>Keywords help the AI find this when the crew phrases it differently than the title. Leave blank if the title is obvious.</div>
              <div>
                <label style={lbl}>{KINDS[editing.kind].lines}</label>
                <textarea value={editing.itemsText} onChange={e => setEditing(s => ({ ...s, itemsText: e.target.value }))} rows={10}
                  placeholder={editing.kind === 'loadup' ? '2 — 3/4" ground rods\n1 roll — mule tape\n10 — wedge caps\nForm wood — 4 sheets\nAnchor bolt template' : 'Call in USA / One-Call locate\nConfirm rebar cage size + depth\nSet anchor bolt template to grade\nVerify plumb + orientation\nPour + finish top of foundation'}
                  style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              </div>
              {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setEditing(null)} style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: KINDS[editing.kind].color, color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : `Save ${KINDS[editing.kind].one}`}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
